import React, { useState, useEffect, useRef } from "react";
import Navbar from "../navbar/Navbar";
import Footer from "../../footer/Footer";
import Client1 from "../../../Assets/PhotoFrame1.png";
import Client2 from "../../../Assets/PhotoFrame2.png";
import Client3 from "../../../Assets/PhotoFrame3.png";
import Client4 from "../../../Assets/PhotoFrame4.png";
import "./HomePage.css"; 
import HomePagImg from "../../../Assets/puphub_home_imag.jpeg"; 
import { Helmet } from "react-helmet-async";

interface Review {
  id: number;
  name: string;
  role: string;
  message: string;
  image: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const reviews: Review[] = [
  {
    id: 1,
    name: "Jane Brown",
    role: "App Tester",
    message: "PupHub is a wonderful pet community platform. The way users can connect, share stories, and explore nearby pet services is impressive!",
    image: Client1,
  },
  {
    id: 2,
    name: "David Lee",
    role: "App Tester",
    message: "Loved how easily I could explore pet stories, chat with other pet parents, and find nearby services using the PIN code.",
    image: Client2,
  },
  {
    id: 3,
    name: "Rahul Tiwary",
    role: "App User",
    message: "PupHub truly feels like a social network for pets and their parents. It's amazing!",
    image: Client3,
  },
  {
    id: 4,
    name: "Palvir Singh",
    role: "App User",
    message: "It's easy to use and packed with features like chatting, blogging, and finding local pet services.",
    image: Client4,
  },
];

const features: Feature[] = [
  { 
    icon: "📸",
    title: "Share Pet Stories", 
    description: "Upload your adorable pet moments and share them with the community." 
  },
  { 
    icon: "📝",
    title: "Write Blogs", 
    description: "Express your pet experiences through beautiful blogs and articles." 
  },
  { 
    icon: "📰",
    title: "Read News", 
    description: "Stay updated with the latest pet-related news and updates." 
  },
  { 
    icon: "💬",
    title: "Chat with Pet Owners", 
    description: "Connect and chat with fellow pet parents nearby." 
  },
  {
    icon: "📍",
    title: "Find Nearby Services",
    description: "Discover pet services in your area with our location-based search."
  },
  {
    icon: "❤️",
    title: "Build Connections",
    description: "Create lasting friendships with other pet lovers in your community."
  }
];

const HomePage: React.FC = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState<number>(0);
  const [featureInView, setFeatureInView] = useState<boolean[]>(Array(features.length).fill(false));
  const featureObserverRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Auto-rotate reviews
    const reviewInterval = setInterval(() => {
      setCurrentReviewIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, 5000);

    // Set up intersection observer for feature cards
    featureObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            const index = parseInt(entry.target.dataset.index || '0', 10);
            setFeatureInView((prev) => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    const featureCards = document.querySelectorAll('.feature-card');
    featureCards.forEach((card, index) => {
      if (card instanceof HTMLElement) {
        card.dataset.index = index.toString();
        featureObserverRef.current?.observe(card);
      }
    });

    return () => {
      clearInterval(reviewInterval);
      featureObserverRef.current?.disconnect();
    };
  }, []);

  const currentReview = reviews[currentReviewIndex];

  const handleDotClick = (index: number): void => {
    setCurrentReviewIndex(index);
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://puphub.co.in/puphub" />
        <title>PupHub | Home</title>
        <meta
          name="description"
          content="Welcome to PupHub, your pet-friendly companion app."
        />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <div
        className="w-[100vw] h-[30vh] sm:h-[70vh] relative"
        style={{
          backgroundImage:
            "url('https://puphub-customer.s3.ap-south-1.amazonaws.com/static_data/images/puphub_home_imag.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        role="img"
        aria-label="Happy pets and their owners using PupHub app"
      />

      {/* Features Section */}
      <section className="features-section" aria-labelledby="features-heading">
        <h2 id="features-heading" className="section-title">
          Why Choose PupHub?
        </h2>
        <div className="features-container">
          {features.map((feature, index) => (
            <div
              key={`feature-${index}`}
              className={`feature-card ${
                featureInView[index] ? "feature-visible" : ""
              }`}
              data-index={index}
            >
              <div className="feature-icon" aria-hidden="true">
                {feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        className="testimonials-section"
        aria-labelledby="testimonials-heading"
      >
        <h2 id="testimonials-heading" className="section-title">
          What Our Users Say
        </h2>
        <div className="testimonial-card">
          <div className="testimonial-image-container">
            <img
              src={currentReview.image}
              alt={`${currentReview.name}, ${currentReview.role}`}
              className="testimonial-image"
              loading="lazy"
            />
          </div>
          <div className="testimonial-content">
            <blockquote className="testimonial-text">
              "{currentReview.message}"
            </blockquote>
            <div className="testimonial-author">
              <p className="author-name">{currentReview.name}</p>
              <p className="author-role">{currentReview.role}</p>
            </div>
          </div>
        </div>
        <div className="testimonial-dots">
          {reviews.map((_, index) => (
            <button
              key={`dot-${index}`}
              className={`dot ${index === currentReviewIndex ? "active" : ""}`}
              onClick={() => handleDotClick(index)}
              aria-label={`View testimonial from ${reviews[index].name}`}
            />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" aria-labelledby="cta-heading">
        <h2 id="cta-heading" className="cta-title">
          Ready to Join Our Pet Community?
        </h2>
        <p className="cta-text">
          Download PupHub now and start connecting with pet lovers near you!
        </p>
        <a
          href="https://play.google.com/store/apps/details?id=com.puphub"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block"
        >
          <button className="cta-button-primary">Download Now</button>
        </a>
      </section>

      <Footer />
    </>
  );
};

export default HomePage;